/**
 * BenefitTable Component
 *
 * Displays investment benefits with schema supporting:
 * - Benefit categories (cost_reduction, revenue_increase, fte_replacement, etc.)
 * - Frequency (one_time, monthly, quarterly, annually)
 * - Monthly/annual calculated amounts
 * - Quantitative vs qualitative benefits
 */

import { BenefitCategoryLabel, FrequencyLabel } from '@medsuite/models';
import { ColumnDef } from '@tanstack/react-table';
import { type FC, useMemo } from 'react';

import { EditableTable, EditableTableProps } from '../EditableTable/EditableTable';
import { TableActions } from '../TableActions/TableActions';

import type { InvestmentBenefit } from '@medsuite/database';

export interface BenefitTableProps extends Omit<
  EditableTableProps<InvestmentBenefit>,
  'columns' | 'data'
> {
  benefits: InvestmentBenefit[];
  onAdd?: () => void;
  onEdit?: (benefit: InvestmentBenefit) => void;
  onDelete?: (benefit: InvestmentBenefit) => void;
  readOnly?: boolean;
  showTotals?: boolean;
}

/**
 * Calculate monthly amount for a benefit based on its category and frequency
 */
function calculateMonthlyAmount(benefit: InvestmentBenefit): number {
  if (!benefit.is_quantifiable) {
    return 0;
  }

  // Handle FTE replacement
  if (benefit.benefit_category === 'fte_replacement') {
    if (benefit.annual_salary && benefit.fte_count) {
      return (Number(benefit.fte_count) * Number(benefit.annual_salary)) / 12;
    }
    if (benefit.hourly_rate && benefit.fte_count) {
      // 2080 hours/year for full FTE
      return (Number(benefit.fte_count) * Number(benefit.hourly_rate) * 2080) / 12;
    }
  }

  // Handle efficiency gain
  if (benefit.benefit_category === 'efficiency_gain') {
    if (benefit.hours_saved_per_week && benefit.hourly_rate) {
      return Number(benefit.hours_saved_per_week) * Number(benefit.hourly_rate) * 4.33;
    }
  }

  // Handle revenue increase
  if (benefit.benefit_category === 'revenue_increase') {
    if (
      benefit.revenue_calc_type === 'percentage' &&
      benefit.revenue_percentage &&
      benefit.baseline_revenue
    ) {
      return ((Number(benefit.revenue_percentage) / 100) * Number(benefit.baseline_revenue)) / 12;
    }
  }

  // Handle standard amount with frequency
  const amount = Number(benefit.amount) || 0;
  switch (benefit.frequency) {
    case 'one_time':
      return 0; // One-time benefits are not monthly recurring
    case 'weekly':
      return amount * 4.33;
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'annually':
      return amount / 12;
    default:
      return amount;
  }
}

/**
 * Format currency for display
 */
function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export const BenefitTable: FC<BenefitTableProps> = ({
  benefits,
  onAdd,
  onEdit,
  onDelete,
  readOnly = false,
  showTotals = true,
  ...tableProps
}) => {
  const columns = useMemo<ColumnDef<InvestmentBenefit>[]>(
    () => [
      {
        accessorKey: 'benefit_category',
        header: 'Category',
        size: 140,
        cell: ({ getValue, row }) => {
          const category = getValue() as string | null;
          const isQuantifiable = row.original.is_quantifiable;

          if (category && BenefitCategoryLabel[category as keyof typeof BenefitCategoryLabel]) {
            const colors: Record<string, string> = {
              cost_reduction: 'text-green-700 bg-green-50',
              revenue_increase: 'text-blue-700 bg-blue-50',
              fte_replacement: 'text-purple-700 bg-purple-50',
              efficiency_gain: 'text-cyan-700 bg-cyan-50',
              cost_avoidance: 'text-orange-700 bg-orange-50',
              qualitative: 'text-gray-700 bg-gray-50',
            };
            return (
              <div className="flex flex-col">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${colors[category] || 'text-gray-600 bg-gray-50'}`}
                >
                  {BenefitCategoryLabel[category as keyof typeof BenefitCategoryLabel]}
                </span>
                {!isQuantifiable && category !== 'qualitative' && (
                  <span className="text-xs text-gray-500 mt-1">Qualitative</span>
                )}
              </div>
            );
          }

          return (
            <span
              className={
                isQuantifiable ? 'text-green-600 font-medium' : 'text-purple-600 font-medium'
              }
            >
              {isQuantifiable ? 'Quantitative' : 'Qualitative'}
            </span>
          );
        },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        size: 200,
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 220,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm line-clamp-2">
            {(getValue() as string) || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'frequency',
        header: 'Frequency',
        size: 100,
        cell: ({ getValue, row }) => {
          if (!row.original.is_quantifiable || row.original.benefit_category === 'qualitative') {
            return <span className="text-gray-400">—</span>;
          }

          const frequency = getValue() as string | null;

          if (frequency && FrequencyLabel[frequency as keyof typeof FrequencyLabel]) {
            const colors: Record<string, string> = {
              one_time: 'text-blue-600 bg-blue-50',
              monthly: 'text-purple-600 bg-purple-50',
              quarterly: 'text-green-600 bg-green-50',
              annually: 'text-orange-600 bg-orange-50',
              weekly: 'text-cyan-600 bg-cyan-50',
            };
            return (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${colors[frequency] || 'text-gray-600 bg-gray-50'}`}
              >
                {FrequencyLabel[frequency as keyof typeof FrequencyLabel]}
              </span>
            );
          }

          return <span className="text-gray-400">—</span>;
        },
      },
      {
        id: 'calculation_details',
        header: 'Details',
        size: 150,
        cell: ({ row }) => {
          const benefit = row.original;

          if (!benefit.is_quantifiable || benefit.benefit_category === 'qualitative') {
            return (
              <span className="text-gray-400 text-xs italic">
                {benefit.qualitative_category || 'Strategic'}
              </span>
            );
          }

          // Show calculation-specific details
          switch (benefit.benefit_category) {
            case 'fte_replacement':
              if (benefit.fte_count) {
                return (
                  <span className="text-xs text-gray-600">
                    {benefit.fte_count} FTE @{' '}
                    {benefit.annual_salary
                      ? formatCurrency(Number(benefit.annual_salary))
                      : `$${benefit.hourly_rate}/hr`}
                  </span>
                );
              }
              break;
            case 'efficiency_gain':
              if (benefit.hours_saved_per_week && benefit.hourly_rate) {
                return (
                  <span className="text-xs text-gray-600">
                    {benefit.hours_saved_per_week} hrs/wk @ ${benefit.hourly_rate}/hr
                  </span>
                );
              }
              break;
            case 'revenue_increase':
              if (benefit.revenue_calc_type === 'percentage') {
                return (
                  <span className="text-xs text-gray-600">
                    {benefit.revenue_percentage}% of{' '}
                    {formatCurrency(Number(benefit.baseline_revenue))}
                  </span>
                );
              }
              break;
          }

          // Default: show amount type
          if (benefit.amount_type) {
            return (
              <span className="text-xs text-gray-600">
                {benefit.amount_type === 'C' ? 'CapEx Savings' : 'OpEx Savings'}
              </span>
            );
          }

          return <span className="text-gray-400">—</span>;
        },
      },
      {
        id: 'monthly_amount',
        header: 'Monthly',
        size: 110,
        cell: ({ row }) => {
          if (!row.original.is_quantifiable || row.original.benefit_category === 'qualitative') {
            return <span className="text-gray-400">—</span>;
          }

          const monthly = calculateMonthlyAmount(row.original);

          if (monthly === 0 && row.original.frequency === 'one_time') {
            const amount = Number(row.original.amount) || 0;
            return (
              <div className="text-right">
                <span className="text-gray-600 font-mono">{formatCurrency(amount)}</span>
                <span className="text-xs text-gray-400 block">one-time</span>
              </div>
            );
          }
          return (
            <div className="text-right font-mono text-gray-900">{formatCurrency(monthly)}</div>
          );
        },
      },
      {
        id: 'annual_amount',
        header: 'Annual',
        size: 120,
        cell: ({ row }) => {
          if (!row.original.is_quantifiable || row.original.benefit_category === 'qualitative') {
            return <span className="text-gray-400">—</span>;
          }

          const monthly = calculateMonthlyAmount(row.original);

          if (row.original.frequency === 'one_time') {
            const amount = Number(row.original.amount) || 0;
            return (
              <div className="text-right font-mono font-semibold text-green-600">
                {formatCurrency(amount)}
              </div>
            );
          }

          return (
            <div className="text-right font-mono font-semibold text-green-600">
              {formatCurrency(monthly * 12)}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 80,
        cell: ({ row }) => {
          const benefit = row.original;
          return (
            <TableActions
              onEdit={onEdit ? () => onEdit(benefit) : undefined}
              onDelete={onDelete ? () => onDelete(benefit) : undefined}
              size="sm"
            />
          );
        },
      },
    ],
    [onEdit, onDelete]
  );

  const totals = useMemo(() => {
    if (!showTotals) {
      return null;
    }

    const quantitative = benefits.filter(
      (b) => b.is_quantifiable !== false && b.benefit_category !== 'qualitative'
    );
    const qualitative = benefits.filter(
      (b) => !b.is_quantifiable || b.benefit_category === 'qualitative'
    );

    const monthlyTotal = quantitative.reduce((sum, b) => sum + calculateMonthlyAmount(b), 0);

    const oneTimeTotal = quantitative
      .filter((b) => b.frequency === 'one_time')
      .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

    // Group by category
    const byCategory = quantitative.reduce(
      (acc, b) => {
        const cat = b.benefit_category || 'other';
        if (!acc[cat]) {
          acc[cat] = 0;
        }
        acc[cat] += calculateMonthlyAmount(b) * 12;
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      quantitativeCount: quantitative.length,
      qualitativeCount: qualitative.length,
      monthlyTotal,
      annualTotal: monthlyTotal * 12 + oneTimeTotal,
      byCategory,
    };
  }, [benefits, showTotals]);

  return (
    <div className="space-y-3">
      {onAdd && !readOnly && (
        <div className="flex justify-end">
          <button
            onClick={onAdd}
            className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            Add Benefit
          </button>
        </div>
      )}
      <EditableTable
        columns={columns}
        data={benefits}
        readOnly={readOnly}
        emptyMessage="No benefits added yet"
        compact
        {...tableProps}
      />
      {showTotals && totals && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Benefits
              </div>
              <div className="mt-1 text-xl font-bold text-gray-900">
                {totals.quantitativeCount} quantitative
              </div>
              {totals.qualitativeCount > 0 && (
                <div className="text-sm text-purple-600">{totals.qualitativeCount} qualitative</div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Monthly Total
              </div>
              <div className="mt-1 text-xl font-bold text-gray-900">
                {formatCurrency(totals.monthlyTotal)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Annual Total
              </div>
              <div className="mt-1 text-xl font-bold text-green-600">
                {formatCurrency(totals.annualTotal)}
              </div>
            </div>
            <div className="border-l pl-4">
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                By Category
              </div>
              <div className="mt-1 space-y-1 text-xs">
                {Object.entries(totals.byCategory)
                  .filter(([_, v]) => v > 0)
                  .sort(([_, a], [__, b]) => b - a)
                  .slice(0, 3)
                  .map(([cat, amount]) => (
                    <div key={cat} className="flex justify-between">
                      <span className="text-gray-600 capitalize">{cat.replace('_', ' ')}:</span>
                      <span className="font-mono">{formatCurrency(amount)}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
